import { Vector2 } from "../../../common/vector2.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { Easing, TransformBase } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { TransformValue } from "../../../transforms/transform_value.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";
import { Vfx2DRuntime } from "../../../webgl_2d/vfx_2d_runtime.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UICanvasRect } from "../../common_ui_elements/ui_canvas_rect.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AdventureBox, AdventureBoxArt } from "./adventure_box_art.js";
import { AdventureRewardHud, AdventureRewardHudRefs } from "./adventure_reward_hud.js";
import { ADVENTURE_REWARD_ICONS, AdventureBoxReward } from "../../../adventure/adventure_box_rewards.js";
import { rewardAmountText } from "../../../common/reward_format.js";
import { RewardFrame } from "../../common_ui_elements/reward_frame.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";

export interface AdventureBoxOpenOptions
{
  center_x?: number;
  center_y?: number;
  wait_for_start?: boolean;
  show_backdrop?: boolean;
  on_closed?: () => void;
  /**
   * Close (and fire on_closed) as soon as the chest is gone, while the coins
   * are still in the air: they fly on the VFX canvas into whatever HUD sits
   * underneath (the lobby's). Without it the reward HUD bar waits for them.
   */
  release_before_coins_land?: boolean;
  /** Fired on the claim tap, when the chest starts to leave; the host fades its own art with it. */
  on_claim?: ( start_time: number, fade_ms: number ) => void;
  on_rewards_rise_started?: ( start_time: number, duration_ms: number, target_box_top_y: number ) => void;
}

export class AdventureBoxOpenPopup
{
  private readonly ctx: CanvasRenderingContext2D;
  private readonly screen: UIElement;

  private readonly REWARD_GRID_COLS = 3;
  private readonly REWARD_SLOT_W = RewardFrame.DESIGN_W;
  private readonly REWARD_SLOT_H = RewardFrame.DESIGN_H;
  private readonly REWARD_COL_GAP = 28;
  private readonly REWARD_ROW_GAP = 42;
  private readonly REWARD_ICON_SIZE = 174;

  private readonly REWARD_ICONS = ADVENTURE_REWARD_ICONS;

  private readonly FLY_ICON_START_SCALE = 2;
  private readonly FLY_ICON_END_SCALE = 0.4;
  private readonly COIN_FLY_SPEED = 2.2;

  private readonly REWARD_BOX_TOP_GAP = 55;
  private readonly REWARD_GRID_BUTTON_GAP = 70;
  private readonly REWARD_BUTTON_W = 570;
  private readonly REWARD_BUTTON_H = 250;

  private readonly BOX_GLOW_SCALE = 1.7;
  private readonly BOX_GLOW_MAX_ALPHA = 0.5;
  private readonly BOX_GLOW_ROTATION_MS = 14000;

  private readonly BOX_GLOW_TINT: [number, number, number] = [1.0, 0.82, 0.35];
  private tinted_glow_bitmap: ImageBitmap | null = null;
  private readonly glow_layers: UIImage[] = [];

  constructor( ctx: CanvasRenderingContext2D, screen: UIElement )
  {
    this.ctx = ctx;
    this.screen = screen;

    this.prepareTintedGlowBitmap();
  }

  private prepareTintedGlowBitmap(): void
  {
    const source = ImagesHolder.getImage( ImgID.RaysGlow1 );

    const off_canvas = document.createElement( "canvas" );
    off_canvas.width = source.width;
    off_canvas.height = source.height;
    // CPU-backed, so the readback below does not stall on the GPU.
    const off_ctx = off_canvas.getContext( "2d", { willReadFrequently: true } )!;
    off_ctx.drawImage( source, 0, 0 );

    const image_data = off_ctx.getImageData( 0, 0, off_canvas.width, off_canvas.height );
    const data = image_data.data;
    const [tint_r, tint_g, tint_b] = this.BOX_GLOW_TINT;

    for ( let i = 0; i < data.length; i += 4 )
    {
      data[i] = Math.min( 255, Math.floor( data[i] * tint_r ) );
      data[i + 1] = Math.min( 255, Math.floor( data[i + 1] * tint_g ) );
      data[i + 2] = Math.min( 255, Math.floor( data[i + 2] * tint_b ) );
    }

    off_ctx.putImageData( image_data, 0, 0 );
    createImageBitmap( off_canvas ).then( bitmap =>
    {
      this.tinted_glow_bitmap = bitmap;

      for ( const layer of this.glow_layers )
        layer.setImageBitmap( bitmap );
    } );
  }

  open( rewards: AdventureBoxReward[], options: AdventureBoxOpenOptions = {} ): () => void
  {
    const screen_w = this.screen.getRect().getW();
    const screen_h = this.screen.getRect().getH();
    const center_x = options.center_x ?? screen_w / 2;
    const center_y = options.center_y ?? screen_h / 2;

    const backdrop = new UICanvasRect( this.ctx, 0, 0, screen_w, screen_h, 'rgba(0, 0, 0, 0.8)' );
    backdrop.setOpacityImmediate( 0 );
    backdrop.setOnClick( () => {} );
    if ( options.show_backdrop !== false )
      this.screen.addChild( backdrop );

    const hud = AdventureRewardHud.build( this.ctx, this.screen, screen_w );
    hud.row.setOpacityImmediate( 0 );

    const glow = this.createBoxGlow();

    const popup = AdventureBoxArt.createClosed( this.ctx, this.screen, center_x, center_y, 1 );
    const start = (): void =>
    {
      if ( started ) return;
      started = true;
      this.playAnimation( backdrop, popup, glow, hud, center_x, center_y, rewards, options );
    };

    let started = false;
    if ( options.wait_for_start )
      return start;

    popup.main.setOpacityImmediate( 0 );
    popup.top.setOpacityImmediate( 0 );

    const now = performance.now();
    const APPEAR_MS = 70;

    if ( options.show_backdrop !== false )
      backdrop.fadeIn( APPEAR_MS, now, Easing.EaseOutQuad );
    popup.main.fadeIn( APPEAR_MS, now, Easing.EaseOutQuad );

    const top_fade_in = new TransformOpacity( popup.top, true, 0, 1, now, APPEAR_MS, { easing: Easing.EaseOutQuad } );
    top_fade_in.setOnFinished( start );
    popup.top.addTransform( top_fade_in );
    return start;
  }

  private playAnimation( backdrop: UICanvasRect, popup: AdventureBox, glow: UIElementHolder, hud: AdventureRewardHudRefs, center_x: number, center_y: number, rewards: AdventureBoxReward[], options: AdventureBoxOpenOptions ): void
  {
    const SQUASH_MS = 60;
    const UNSQUASH_MS = 60;
    const GROW_MS = 50;
    const SHRINK_MS = 60;

    const SQUASHED = 0.55;
    const GROWN = 1.2;

    // Grow and shrink run with the open cap, so the cap swaps in between - at scale 1,
    // where the swap is invisible.
    const phase2_grow_shrink = () =>
    {
      AdventureBoxArt.swapCapToOpen( popup.top, center_x, center_y, 1 );

      const start = performance.now();
      const grow = { to: GROWN, ms: GROW_MS, easing: Easing.EaseOutQuad };
      const shrink = { to: 1, ms: SHRINK_MS, easing: Easing.EaseInQuad };
      const both_done = this.afterBoth( () => this.showRewards( backdrop, popup, glow, hud, center_x, center_y, rewards, options ) );
      this.bounceFromBottom( popup.main, center_y, grow, shrink, start, both_done );
      this.bounceFromBottom( popup.top, center_y, grow, shrink, start, both_done );
    };

    const start = performance.now();
    const squash = { to: SQUASHED, ms: SQUASH_MS, easing: Easing.EaseInQuad };
    const unsquash = { to: 1, ms: UNSQUASH_MS, easing: Easing.EaseOutQuad };
    // The cap's own last frame must land before the swap, or it resets the open cap
    // back to the closed cap's position.
    const both_done = this.afterBoth( phase2_grow_shrink );
    this.bounceFromBottom( popup.main, center_y, squash, unsquash, start, both_done );
    this.bounceFromBottom( popup.top, center_y, squash, unsquash, start, both_done );
  }

  private afterBoth( fn: () => void ): () => void
  {
    let pending = 2;
    return () =>
    {
      if ( --pending === 0 )
        fn();
    };
  }

  /**
   * Scales Y 1 -> out.to -> back.to keeping ground_y fixed. Scale and the position that
   * pins the bottom are one transform: as two (an important scale plus an additive move)
   * the move's offset dropped out between chained phases while the scale stayed at
   * 0.55/1.2, and the chest jumped for a frame at every phase boundary.
   */
  private bounceFromBottom(
    target: UIElement,
    ground_y: number,
    out: { to: number, ms: number, easing: Easing },
    back: { to: number, ms: number, easing: Easing },
    start_time: number,
    on_complete?: () => void
  ): void
  {
    const base = target.pixelPosImportant().clone();
    const d = base.y + target.getRect().getH() / 2 - ground_y;
    const total_ms = out.ms + back.ms;
    const split = out.ms / total_ms;

    const scaleAt = ( t: number ): number =>
    {
      if ( t <= split )
        return 1 + ( out.to - 1 ) * TransformBase.applyEasing( out.easing, split > 0 ? t / split : 1, 1 );
      const k = TransformBase.applyEasing( back.easing, ( t - split ) / ( 1 - split ), 1 );
      return out.to + ( back.to - out.to ) * k;
    };
    const sample = ( t: number ): { pos: Vector2, scale: Vector2 } =>
    {
      const s = scaleAt( t );
      return { pos: new Vector2( base.x, base.y + d * ( s - 1 ) ), scale: new Vector2( 1, s ) };
    };

    const apply = ( t: number ): void =>
    {
      const { pos, scale } = sample( t );
      target.setScale( scale );
      target.setPixelPos( pos, true );
    };

    const tween = new TransformValue( 0, 1, start_time, total_ms, apply, { easing: Easing.Linear } );
    tween.setDomSample( t => sample( t ) );
    // The last frame can land short of t = 1; the next phase reads this pose as its base.
    tween.setOnFinished( () =>
    {
      apply( 1 );
      on_complete?.();
    } );
    target.addTransform( tween );
  }

  private showRewards( backdrop: UICanvasRect, popup: AdventureBox, glow: UIElementHolder, hud: AdventureRewardHudRefs, center_x: number, center_y: number, rewards: AdventureBoxReward[], options: AdventureBoxOpenOptions ): void
  {
    const RISE_MS = 170;
    const REVEAL_MS = 130;

    const now = performance.now();

    const cap_top_offset = LayoutScaler.heightFromDesign( AdventureBoxArt.MAIN_H ) + LayoutScaler.yFromDesign( AdventureBoxArt.OPEN_OFFSET_Y );
    const box_top_gap = LayoutScaler.yFromDesign( this.REWARD_BOX_TOP_GAP );
    const panel_h = this.rewardsPanelHeight( rewards.length );

    const block_h = cap_top_offset + box_top_gap + panel_h;
    const block_top = this.screen.getRect().getH() / 2 - block_h / 2;
    options.on_rewards_rise_started?.( now, RISE_MS, block_top );
    const rise_offset = center_y - ( block_top + cap_top_offset );

    const main_target = new Vector2( popup.main.pixelPosImportant().x, popup.main.pixelPosImportant().y - rise_offset );
    const top_target = new Vector2( popup.top.pixelPosImportant().x, popup.top.pixelPosImportant().y - rise_offset );

    popup.main.addTransform( new TransformStraightMove( popup.main, true, null, main_target, now, RISE_MS, { easing: Easing.EaseOutQuad } ) );
    popup.top.addTransform( new TransformStraightMove( popup.top, true, null, top_target, now, RISE_MS, { easing: Easing.EaseOutQuad } ) );

    const risen_box_bottom_y = center_y - rise_offset;
    const box_center_y = risen_box_bottom_y - cap_top_offset / 2;

    this.startBoxGlow( glow, center_x, box_center_y, RISE_MS );

    const { panel, claim_button, slot_icons } = this.createRewardsPanel( rewards, center_x, risen_box_bottom_y );

    panel.setOpacityImmediate( 0 );
    panel.fadeIn( REVEAL_MS, now + RISE_MS * 0.4, Easing.EaseOutQuad );

    const HUD_APPEAR_MS = 80;

    claim_button.setOnClick( () =>
    {
      claim_button.setClickable( false );
      hud.row.fadeIn( HUD_APPEAR_MS, performance.now(), Easing.EaseOutQuad );
      const fly = this.flyRewardsToHud( rewards, slot_icons, hud );
      // The chest leaves on the tap itself; the rewards fly out of it on their own.
      this.closeRewards( backdrop, popup, glow, panel, hud, options, performance.now() + fly.icons_ms, fly.coins_end_at );
    } );
  }

  /** icons_ms: until the last non-coin icon lands; coins_end_at: absolute time the last coin lands. */
  private flyRewardsToHud( rewards: AdventureBoxReward[], slot_icons: UIImage[], hud: AdventureRewardHudRefs ): { icons_ms: number, coins_end_at: number }
  {
    Game.instance.setVfxOverlayCanvasesVisible( true );

    const now = performance.now();
    const icon_size = LayoutScaler.widthFromDesign( this.REWARD_ICON_SIZE * 0.75 );

    let icons_finish = now;
    let coins_finish = now;
    let heart_finish = 0;
    let avatar_finish = 0;

    rewards.forEach( ( reward, i ) =>
    {
      const from = this.absoluteCenter( slot_icons[i] );

      if ( reward.type === "coin" )
      {
        const count = Math.max( 1, Math.min( 8, Math.round( reward.amount / 3 ) ) );
        const finish = Vfx2DManager.addCoinsFly( from, hud.coin.target, count, now, this.COIN_FLY_SPEED );
        coins_finish = Math.max( coins_finish, finish );
        return;
      }

      const is_heart_reward = reward.type === "heart" || reward.type === "infinite_hearts";
      const target = is_heart_reward ? hud.heart.target : hud.avatar_target;
      const count = reward.type === "heart" ? Math.min( 3, reward.amount ) : 1;

      for ( let n = 0; n < count; n++ )
      {
        const finish = this.flyIcon( this.REWARD_ICONS[reward.type], from, target, icon_size, now + n * 40 );
        icons_finish = Math.max( icons_finish, finish );
        if ( is_heart_reward ) heart_finish = Math.max( heart_finish, finish );
        else avatar_finish = Math.max( avatar_finish, finish );
      }
    } );

    if ( heart_finish > 0 )
      Vfx2DManager.addCoinCounterImpact( hud.heart.target, icon_size, heart_finish, 280 );
    if ( avatar_finish > 0 )
      Vfx2DManager.addCoinCounterImpact( hud.avatar_target, icon_size * 1.3, avatar_finish, 280 );

    return { icons_ms: icons_finish - now, coins_end_at: coins_finish };
  }

  private flyIcon( img_id: ImgID, from: Vector2, to: Vector2, size: number, start_time: number ): number
  {
    const DURATION_MS = 300;

    const icon = new UIImage( this.ctx, from.x - size / 2, from.y - size / 2, size, size, img_id );
    this.screen.addChild( icon );

    const side = Math.random() < 0.5 ? -1 : 1;
    const mid_x = ( from.x + to.x ) / 2 + side * ( 40 + Math.random() * 50 );
    const mid_y = Math.min( from.y, to.y ) - ( 60 + Math.random() * 50 );

    const path = ( t: number ): Vector2 =>
    {
      const inv = 1 - t;
      const x = inv * inv * from.x + 2 * inv * t * mid_x + t * t * to.x;
      const y = inv * inv * from.y + 2 * inv * t * mid_y + t * t * to.y;
      return new Vector2( x - size / 2, y - size / 2 );
    };
    const apply = ( t: number ): void => icon.setPixelPos( path( t ), true );

    const fly = new TransformValue( 0, 1, start_time, DURATION_MS, apply, { easing: Easing.EaseInQuad } );
    fly.setDomSample( t => ( { pos: path( t ) } ) );
    icon.addTransform( fly );
    const start_scale = new Vector2( this.FLY_ICON_START_SCALE, this.FLY_ICON_START_SCALE );
    const end_scale = new Vector2( this.FLY_ICON_END_SCALE, this.FLY_ICON_END_SCALE );
    icon.addTransform( new TransformScale( icon, true, start_scale, end_scale, start_time, DURATION_MS, { easing: Easing.EaseInQuad } ) );

    const fade = new TransformOpacity( icon, true, 1, 0, start_time + DURATION_MS * 0.75, DURATION_MS * 0.25, { easing: Easing.EaseInQuad } );
    fade.setOnFinished( () => this.screen.removeChild( icon ) );
    icon.addTransform( fade );

    return start_time + DURATION_MS;
  }

  private absoluteCenter( el: UIElement ): Vector2
  {
    const pos = el.getAbsolutePosition();
    const rect = el.getRect();
    return new Vector2( pos.x + rect.getW() / 2, pos.y + rect.getH() / 2 );
  }

  private createBoxGlow(): UIElementHolder
  {
    const size = LayoutScaler.widthFromDesign( AdventureBoxArt.OPEN_W * this.BOX_GLOW_SCALE );

    const glow = new UIElementHolder( this.ctx, 0, 0, size, size, false );
    glow.setOpacityImmediate( 0 );

    const now = performance.now();
    for ( const direction of [1, -1] )
    {
      const layer = new UIImage( this.ctx, 0, 0, size, size, ImgID.RaysGlow1 );
      this.glow_layers.push( layer );
      if ( this.tinted_glow_bitmap )
        layer.setImageBitmap( this.tinted_glow_bitmap );

      layer.enableRotation();
      layer.addTransform( new TransformRotate(
        layer, true, 0, direction * Math.PI * 2, now, this.BOX_GLOW_ROTATION_MS,
        { easing: Easing.Linear, infinite_loop: true, decorative: true }
      ) );

      glow.addChild( layer );
    }

    this.screen.addChild( glow );
    return glow;
  }

  private startBoxGlow( glow: UIElementHolder, center_x: number, center_y: number, appear_ms: number ): void
  {
    const size = LayoutScaler.widthFromDesign( AdventureBoxArt.OPEN_W * this.BOX_GLOW_SCALE );
    glow.setPixelPos( new Vector2( center_x - size / 2, center_y - size / 2 ), true );
    glow.fadeToOpacity( this.BOX_GLOW_MAX_ALPHA, appear_ms, performance.now(), Easing.EaseOutQuad );
  }

  private rewardsPanelHeight( reward_count: number ): number
  {
    const rows = Math.ceil( reward_count / this.REWARD_GRID_COLS );
    const slot_h = LayoutScaler.heightFromDesign( this.REWARD_SLOT_H );
    const row_gap = LayoutScaler.heightFromDesign( this.REWARD_ROW_GAP );
    const grid_h = rows * slot_h + Math.max( 0, rows - 1 ) * row_gap;

    const button_h = LayoutScaler.heightFromDesign( this.REWARD_BUTTON_H );
    const grid_button_gap = LayoutScaler.yFromDesign( this.REWARD_GRID_BUTTON_GAP );

    return grid_h + grid_button_gap + button_h;
  }

  private createRewardsPanel( rewards: AdventureBoxReward[], center_x: number, box_bottom_y: number ): { panel: UIElementHolder, claim_button: UIImage, slot_icons: UIImage[] }
  {
    const rows = Math.ceil( rewards.length / this.REWARD_GRID_COLS );
    const slot_w = LayoutScaler.widthFromDesign( this.REWARD_SLOT_W );
    const slot_h = LayoutScaler.heightFromDesign( this.REWARD_SLOT_H );
    const col_gap = LayoutScaler.widthFromDesign( this.REWARD_COL_GAP );
    const row_gap = LayoutScaler.heightFromDesign( this.REWARD_ROW_GAP );

    const grid_w = this.REWARD_GRID_COLS * slot_w + ( this.REWARD_GRID_COLS - 1 ) * col_gap;
    const grid_h = rows * slot_h + Math.max( 0, rows - 1 ) * row_gap;

    const button_w = LayoutScaler.widthFromDesign( this.REWARD_BUTTON_W );
    const button_h = LayoutScaler.heightFromDesign( this.REWARD_BUTTON_H );

    const box_top_gap = LayoutScaler.yFromDesign( this.REWARD_BOX_TOP_GAP );
    const grid_button_gap = LayoutScaler.yFromDesign( this.REWARD_GRID_BUTTON_GAP );

    const panel_w = Math.max( grid_w, button_w );
    const panel_h = grid_h + grid_button_gap + button_h;

    const panel_x = center_x - panel_w / 2;
    const panel_y = box_bottom_y + box_top_gap;

    const panel = new UIElementHolder( this.ctx, panel_x, panel_y, panel_w, panel_h, false );

    const grid_x = ( panel_w - grid_w ) / 2;
    const slot_icons: UIImage[] = rewards.map( ( reward, i ) =>
    {
      const row = Math.floor( i / this.REWARD_GRID_COLS );
      const col = i % this.REWARD_GRID_COLS;

      const items_in_row = Math.min( this.REWARD_GRID_COLS, rewards.length - row * this.REWARD_GRID_COLS );
      const row_w = items_in_row * slot_w + ( items_in_row - 1 ) * col_gap;
      const row_x = grid_x + ( grid_w - row_w ) / 2;

      return this.createRewardSlot( panel, reward, row_x + col * ( slot_w + col_gap ), row * ( slot_h + row_gap ) );
    } );

    const button_x = ( panel_w - button_w ) / 2;
    const button_y = grid_h + grid_button_gap;
    const claim_button = this.createClaimButton( panel, button_x, button_y, button_w, button_h );

    this.screen.addChild( panel );

    return { panel, claim_button, slot_icons };
  }

  private createRewardSlot( panel: UIElement, reward: AdventureBoxReward, x: number, y: number ): UIImage
  {
    const is_coin = reward.type === "coin";
    const icon_w = LayoutScaler.widthFromDesign( is_coin ? 210 : this.REWARD_ICON_SIZE );
    const icon_h = LayoutScaler.heightFromDesign( is_coin ? 168 : this.REWARD_ICON_SIZE );
    const frame = new RewardFrame( this.ctx, x, y, {
      icon_id: is_coin ? ImgID.UI_ShopBlockImg : this.REWARD_ICONS[reward.type],
      amount_text: is_coin ? `${reward.amount}` : rewardAmountText( reward.type, reward.amount ),
      icon_width: icon_w,
      icon_height: icon_h,
    } );
    RewardTooltip.attachLegacy( frame, reward.type );
    panel.addChild( frame );

    return frame.icon;
  }

  private createClaimButton( panel: UIElement, x: number, y: number, w: number, h: number ): UIImage
  {
    const button = new UIImage( this.ctx, x, y, w, h, ImgID.UI_PlayButton, {}, true );

    const text = new UITextElement( this.ctx, 0, 0, w, h, {
      localization_key: "CLAIM",
      font_size: LayoutScaler.textSizeFromDesign( 67 ),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      text_style: TextStyleScheme.geologica,
      text_color: ColorScheme.white,
      font_weight: 800,
      shadow_blur: LayoutScaler.widthFromDesign( 6 ),
      shadow_color: ColorScheme.black_transparent,
      shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      outline_color: ColorScheme.more_lives_buy_outline,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
    } );

    button.addChild( text, true );
    panel.addChild( button );

    return button;
  }

  private closeRewards( backdrop: UICanvasRect, popup: AdventureBox, glow: UIElementHolder, panel: UIElementHolder, hud: AdventureRewardHudRefs, options: AdventureBoxOpenOptions, icons_end_at: number, coins_end_at: number ): void
  {
    const FADE_OUT_MS = 90;
    const now = performance.now();
    const release = options.release_before_coins_land === true;
    options.on_claim?.( now, FADE_OUT_MS );

    if ( options.show_backdrop !== false )
      backdrop.fadeOut( FADE_OUT_MS, now, Easing.EaseInQuad );
    popup.main.fadeOut( FADE_OUT_MS, now, Easing.EaseInQuad );
    popup.top.fadeOut( FADE_OUT_MS, now, Easing.EaseInQuad );
    glow.fadeOut( FADE_OUT_MS, now, Easing.EaseInQuad );

    const panel_fade_out = new TransformOpacity( panel, true, 1, 0, now, FADE_OUT_MS, { easing: Easing.EaseInQuad } );
    panel_fade_out.setOnFinished( () =>
    {
      if ( options.show_backdrop !== false )
        this.screen.removeChild( backdrop );
      this.screen.removeChild( popup.main );
      this.screen.removeChild( popup.top );
      this.screen.removeChild( glow );
      this.screen.removeChild( panel );

      if ( release )
      {
        // Hearts and boosters fly as children of this screen, so it stays
        // (visually empty but for them and the HUD bar, which sits over the
        // lobby's own) until they land; the coins go on without it.
        window.setTimeout( () =>
        {
          this.screen.removeChild( hud.row );
          this.hideVfxOverlayAfter( coins_end_at );
          options.on_closed?.();
        }, Math.max( 0, icons_end_at - performance.now() ) );
        return;
      }

      // Nothing under the popup counts coins here, so the HUD bar stays
      // until they have landed in it.
      const hud_fade_at = Math.max( performance.now(), coins_end_at + 120 );
      const hud_fade_out = new TransformOpacity( hud.row, true, 1, 0, hud_fade_at, FADE_OUT_MS, { easing: Easing.EaseInQuad } );
      hud_fade_out.setOnFinished( () =>
      {
        this.screen.removeChild( hud.row );
        this.hideVfxOverlayAfter( 0 );
        options.on_closed?.();
      } );
      hud.row.addTransform( hud_fade_out );
    } );
    panel.addTransform( panel_fade_out );
  }

  /** Hides the VFX overlay canvases once the effects still in flight have finished. */
  private hideVfxOverlayAfter( end_at: number ): void
  {
    window.setTimeout( () =>
    {
      try
      {
        if ( !Vfx2DRuntime.hasActiveEffects() )
          Game.instance.setVfxOverlayCanvasesVisible( false );
      }
      catch ( e )
      {
        console.warn( "AdventureBoxOpenPopup.closeRewards: hasActiveEffects() check failed, leaving VFX overlay canvases as-is", e );
      }
    }, Math.max( 0, end_at - performance.now() ) + 50 );
  }
}
